import { LVG_PERSISTANCE_STATE } from 'constants/index'
import { LVG_ASSETS, getAssetsForGame } from 'constants/games/lvg/lvgAssets'
import { atom, useAtomValue, useSetAtom } from 'jotai'
import { focusAtom } from 'jotai-optics'
import { atomWithStorage } from 'jotai/utils'
import { useMemo } from 'react'
import { isEmpty } from 'lodash'
import { useYoloToast } from 'lib/yoloToasts/useYoloToast'
import ms from 'ms.macro'
import { useDebouncedCallback } from 'utils/hooks/useDebounceCallback'
import { useLayoutV3State } from 'hooks/useLayoutV3State'
import { CONTENT_TYPES } from 'constants/games/contentTypes'

const DEFAULT_ASSETS_ID = {
  [CONTENT_TYPES.LVG_CRYPTO]: 'BTC/USD',
  [CONTENT_TYPES.LVG_STOCKS]: 'TSLA',
  [CONTENT_TYPES.LVG_INDEXES]: 'DINO',
  [CONTENT_TYPES.LVG_AI_INDEXES]: 'AutoGPT',
}

const INIT_PERSISTENCE_STATE = {
  activeAssetId: {},
  activeGraphId: 0,
}

const INIT_VOLATILE_STATE = {
  activeOrder: {},
  liveOrder: {},
}

const lvgPersistenceStateAtom = atomWithStorage(LVG_PERSISTANCE_STATE, INIT_PERSISTENCE_STATE)
const lvgVolatileStateAtom = atom(INIT_VOLATILE_STATE)

const updateActiveAssetAtom = atom(null, (get, set, lvgGameType, selectedAssetId) => {
  set(lvgPersistenceStateAtom, (prev) => {
    // Cross Atom update
    set(lvgVolatileStateAtom, (prev) => ({ ...prev, activeOrder: {} }))

    // Atom update
    const activeAssetId = { ...prev.activeAssetId, [lvgGameType]: selectedAssetId }
    return { ...prev, activeAssetId }
  })
})

//currentGame, order)//

const updateActiveOrderAtom = atom(null, (get, set, lvgGameType, selectedOrder, disableToggle) => {
  set(lvgVolatileStateAtom, (prev) => {
    // Check if the order belongs to the current active game
    const newLvgGameType = lvgGameType.toLowerCase().replace('lvg_', 'lvg-')
    const gameAssets = getAssetsForGame(newLvgGameType)
    const orderAssetInfo = gameAssets.find((asset) => asset.id === selectedOrder?.asset)
    if (!orderAssetInfo) return { ...prev, activeOrder: {} }
    
    // This toggle the active order selection
    if (!disableToggle && prev.activeOrder?.uuid === selectedOrder?.uuid) return { ...prev, activeOrder: {} }
    
    // Cross Atom update
    set(lvgPersistenceStateAtom, (prev) => {
      // if (!orderAssetInfo) return prev
      // console.log('ACZ: kk1', { [lvgGameType]: orderAssetInfo.id })
      const activeAssetId = { ...prev.activeAssetId, [lvgGameType]: orderAssetInfo.id }
      return { ...prev, activeAssetId }
    })
    
    // Atom Update
    return { ...prev, activeOrder: selectedOrder }
  })
})

const updateActiveGraphIdAtom = focusAtom(lvgPersistenceStateAtom, (optic) => optic.prop('activeGraphId'))
const updateLiveOrderAtom = focusAtom(lvgVolatileStateAtom, (optic) => optic.prop('liveOrder'))

export const useLvgState = () => {
  const { yToast } = useYoloToast()
  const { currentGame, setCurrentGame } = useLayoutV3State()
  const persistanceState = useAtomValue(lvgPersistenceStateAtom)
  const volatileState = useAtomValue(lvgVolatileStateAtom)
  const setActiveGraphId = useSetAtom(updateActiveGraphIdAtom)
  const setActiveAssetState = useSetAtom(updateActiveAssetAtom)
  const setActiveOrderState = useSetAtom(updateActiveOrderAtom)
  const setLiveOrder = useSetAtom(updateLiveOrderAtom)

  const yToastDebounced = useDebouncedCallback((toastObj) => yToast(toastObj), 300)

  const setActiveAsset = (asset, game) => {
    if (!isEmpty(asset?.actions)) {
      asset.actions.forEach((action) => {
        switch (action.actionId) {
          case 'showToastOnAssetChange':
            const toastPayload = {
              id: 'genericToast',
              autoClose: action.autoClose || ms`15s`,
              props: {
                message: { title: action.title, subtitle: action.subtitle, icon: asset.icon },
              },
            }
            yToastDebounced(toastPayload)
            break
          default:
            break
        }
      })
    }
    setActiveAssetState(game || currentGame, asset.id)
    game && setCurrentGame(game)
  }

  const setActiveOrder = (order) => {
    // {lvgGameType, selectedOrder, disableToggle}
    console.log('ACZ: setOrder', order)
    setActiveOrderState(currentGame, order)
  }

  const updateActiveOrder = (order) => {
    console.log('ACZ: order', order)
    setActiveOrderState(currentGame, order, true)
  }

  const resetActiveOrder = () => {
    setActiveOrderState(currentGame, {})
  }

  const activeAsset = useMemo(() => {
    const lsAssetId = persistanceState?.activeAssetId?.[currentGame]
    let asset = LVG_ASSETS.find((asset) => asset.id === lsAssetId)
    if (!asset) asset = LVG_ASSETS.find((asset) => asset.id === DEFAULT_ASSETS_ID[currentGame])
    return asset || {}
  }, [currentGame, persistanceState?.activeAssetId])

  return {
    ...persistanceState,
    ...volatileState,
    activeAsset,
    setActiveAsset,
    setActiveGraphId,
    setActiveOrder,
    updateActiveOrder,
    resetActiveOrder,
    setLiveOrder,
  }
}
